// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Group, Loader, Pagination, Stack, Table, Text } from '@mantine/core';
import { IconFileText } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { AppointmentDetails } from '../../hooks/useAppointmentDetails';
import { formatAppointmentDate, formatCopay } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { TableAnchor } from '../layout/TableAnchor';

const BRAND = '#640040';

const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  booked: 'Scheduled',
  fulfilled: 'Completed',
  cancelled: 'Cancelled',
  noshow: 'No Show',
};

const ITEMS_PER_PAGE = 5;

interface AppointmentHistoryTableProps {
  /** Enriched history appointments to display (already filtered to `fulfilled`). */
  appointments: AppointmentDetails[];
  /** Whether history data is still loading */
  loading: boolean;
  /** Patient ID — used to build patient-scoped links for notes & documents */
  patientId: string;
}

function DocumentLinksCell({ details, patientId }: { details: AppointmentDetails; patientId: string }): JSX.Element {
  if (details.documents.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        None
      </Text>
    );
  }

  return (
    <Stack gap={4}>
      {details.documents.map((doc) => (
        <TableAnchor key={doc.id} size="sm" href={`/Patient/${patientId}/DocumentReference/${doc.id}`}>
          <Group gap={4} wrap="nowrap">
            <IconFileText size={14} />
            <span>{doc.title}</span>
          </Group>
        </TableAnchor>
      ))}
    </Stack>
  );
}

/**
 * Table displaying a patient's past (`fulfilled`) appointments from the
 * clinician's point of view.
 *
 * Provider-context differences vs. the patient-app version:
 *   - Columns: Type / Date / Time / Copay / Provider / **Documentation** / Actions
 *     (no standalone Payment Status column).
 *   - Documentation links point to `/Patient/:patientId/DocumentReference/:id`
 *     so they stay inside the patient page.
 *   - Actions column shows a single "View Note" button that opens the linked
 *     Encounter at `/Patient/:patientId/Encounter/:encounterId`. Disabled when
 *     no encounter is attached. Reschedule / Cancel are intentionally hidden
 *     for fulfilled appointments.
 *   - Pagination at 5 items per page.
 * @param props - Component props
 * @returns The appointment history table
 */
export function AppointmentHistoryTable({
  appointments,
  loading,
  patientId,
}: AppointmentHistoryTableProps): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  if (appointments.length === 0) {
    return (
      <Text c="dimmed" py="md" ta="center">
        No past appointments.
      </Text>
    );
  }

  const totalPages = Math.ceil(appointments.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const pageItems = appointments.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const showing = Math.min(startIndex + ITEMS_PER_PAGE, appointments.length);

  return (
    <>
      <Table highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Time</Table.Th>
            <Table.Th>Copay</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Documentation</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {pageItems.map((details) => {
            const encounterId = details.encounter?.id;
            return (
              <Table.Tr key={details.appointment.id}>
                <Table.Td>
                  <Text size="sm">{details.appointmentType}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={BRAND} variant="light" size="sm">
                    {APPOINTMENT_STATUS_LABELS[details.appointment.status as string] ?? details.appointment.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatAppointmentDate(details.appointment.start)}</Text>
                </Table.Td>
                <Table.Td>
                  <AppointmentTimeWithTimezone
                    start={details.appointment.start}
                    end={details.appointment.end}
                    textProps={{ size: 'sm' }}
                  />
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatCopay(details.copay)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{details.providerName}</Text>
                </Table.Td>
                <Table.Td>
                  <DocumentLinksCell details={details} patientId={patientId} />
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconFileText size={14} />}
                    disabled={!encounterId}
                    onClick={() => encounterId && navigate(`/Patient/${patientId}/Encounter/${encounterId}`)}
                    title={!encounterId ? 'No encounter linked to this appointment' : undefined}
                  >
                    View Note
                  </Button>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {showing} of {appointments.length}
          </Text>
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}
    </>
  );
}
